import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { isPro, isSubscribed, markSubscribed } from '../utils/featureGate';
import ShareBar from '../components/ShareBar';
import SEO from '../components/SEO';
import { Helmet } from 'react-helmet-async';
import EmailCapture from '../components/EmailCapture';
import { useIsMobile } from '../hooks/useWindowWidth';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5001';

// Shared styles used by helper components outside the main Brief component
const DS = {
  digestCard: { background: '#1e293b', borderRadius: '16px', border: '1px solid #334155', overflow: 'hidden', position: 'relative', zIndex: 1 },
  digestHeader: { padding: '28px 32px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', borderBottom: '1px solid #334155' },
  digestEyebrow: { fontSize: '11px', fontWeight: '700', color: '#38bdf8', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' },
  digestTitle: { fontSize: '22px', fontWeight: '800', color: '#f1f5f9', margin: '0 0 6px', letterSpacing: '-0.3px' },
  digestDate: { color: '#475569', fontSize: '13px' },
  digestBody: { padding: '24px 32px' },
  digestPara: { color: '#94a3b8', fontSize: '15px', lineHeight: 1.75, marginBottom: '14px' },
  digestUnlockBtn: { display: 'inline-block', background: '#38bdf8', color: '#0f172a', padding: '12px 28px', borderRadius: '10px', fontWeight: '700', fontSize: '15px', textDecoration: 'none' },
  proWall: { background: 'linear-gradient(135deg, rgba(56,189,248,0.06), rgba(99,102,241,0.06))', border: '2px solid rgba(56,189,248,0.2)', borderRadius: '16px', padding: '40px', textAlign: 'center', position: 'relative', zIndex: 1 },
  proWallIcon: { fontSize: '48px', marginBottom: '16px' },
  proWallTitle: { fontSize: '24px', fontWeight: '800', color: '#f1f5f9', margin: '0 0 12px' },
  proWallDesc: { color: '#94a3b8', fontSize: '16px', lineHeight: 1.65, maxWidth: '480px', margin: '0 auto 24px' },
  proWallFeatures: { display: 'flex', flexDirection: 'column', gap: '8px', maxWidth: '380px', margin: '0 auto 28px', textAlign: 'left' },
  proWallFeat: { color: '#64748b', fontSize: '14px', padding: '6px 0', borderBottom: '1px solid #1e293b' },
  proWallBtn: { display: 'inline-block', background: '#38bdf8', color: '#0f172a', padding: '14px 32px', borderRadius: '10px', fontWeight: '800', fontSize: '16px', textDecoration: 'none' },
  proWallNote: { color: '#334155', fontSize: '12px', marginTop: '12px' },
  moodBadge: { padding: '4px 12px', borderRadius: '6px', fontSize: '12px', fontWeight: '700' },
  archiveWrap:      { marginTop: '32px', position: 'relative', zIndex: 1 },
  archiveToggle:    { width: '100%', background: 'rgba(255,255,255,0.03)', border: '1px solid #334155', color: '#64748b', padding: '14px 20px', borderRadius: '12px', cursor: 'pointer', fontFamily: 'inherit', fontSize: '14px', fontWeight: '600', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  archiveList:      { marginTop: '8px', background: '#1e293b', borderRadius: '12px', border: '1px solid #334155', overflow: 'hidden' },
  archiveItem:      { width: '100%', background: 'transparent', border: 'none', borderBottom: '1px solid #0f172a', color: '#94a3b8', padding: '14px 20px', cursor: 'pointer', fontFamily: 'inherit', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '12px', textAlign: 'left' },
  archiveItemDate:  { flex: 1, fontWeight: '600', color: '#f1f5f9', fontSize: '13px' },
  archiveItemTitle: { flex: 1, color: '#64748b', fontSize: '12px' },
  archiveItemArrow: { color: '#334155', fontSize: '14px' },
  archiveBack:      { background: 'transparent', border: 'none', color: '#38bdf8', cursor: 'pointer', fontFamily: 'inherit', fontSize: '13px', padding: '0 0 16px', display: 'block' },
  // SnapCard / MarketSnapshot
  snapshotGrid:      { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: '7px' },
  snapCard:          { borderRadius: '8px', padding: '9px 8px', border: '1px solid', textAlign: 'center' },
  snapEmoji:         { fontSize: '14px', marginBottom: '3px', lineHeight: 1 },
  snapSym:           { fontSize: '10px', fontWeight: '700', color: '#64748b', letterSpacing: '0.3px', marginBottom: '5px' },
  snapPrice:         { fontSize: '12px', fontWeight: '800', color: '#f8fafc', marginBottom: '2px' },
  snapChange:        { fontSize: '10px', fontWeight: '700' },
  snapUpgradeOverlay:{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(15,23,42,0.8)', backdropFilter: 'blur(2px)', borderRadius: '8px' },
  snapUpgradeInner:  { textAlign: 'center', padding: '8px' },
  snapUpgradeTitle:  { fontSize: '12px', fontWeight: '800', color: '#f1f5f9', marginBottom: '4px' },
  snapUpgradeSub:    { fontSize: '11px', color: '#64748b', marginBottom: '10px' },
  snapUpgradeBtn:    { background: 'linear-gradient(135deg, #38bdf8, #6366f1)', color: '#fff', padding: '7px 14px', borderRadius: '7px', fontWeight: '700', fontSize: '11px', textDecoration: 'none', display: 'inline-block' },
  // StoryCard
  storyCard:          { background: '#1e293b', borderRadius: '14px', border: '1px solid #334155', overflow: 'hidden' },
  storyCardOpen:      { border: '1px solid rgba(56,189,248,0.2)' },
  storyHead:          { width: '100%', background: 'transparent', border: 'none', padding: '20px 24px', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px', cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left' },
  storyHeadLeft:      { display: 'flex', alignItems: 'flex-start', gap: '14px', flex: 1 },
  storyNum:           { background: 'rgba(56,189,248,0.1)', color: '#38bdf8', borderRadius: '6px', padding: '2px 8px', fontSize: '11px', fontWeight: '800', flexShrink: 0, marginTop: '2px' },
  storyTitle:         { fontSize: '16px', fontWeight: '700', color: '#f1f5f9', lineHeight: 1.4, marginBottom: '8px' },
  storyMeta:          { display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' },
  storyCat:           { color: '#475569', fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.4px' },
  storyBody:          { padding: '0 24px 24px 56px', display: 'flex', flexDirection: 'column', gap: '16px' },
  storySection:       { borderRadius: '10px', padding: '14px 16px', background: 'rgba(15,23,42,0.5)' },
  storySectionLabel:  { fontSize: '11px', fontWeight: '700', color: '#38bdf8', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' },
  storyText:          { color: '#94a3b8', fontSize: '14px', lineHeight: 1.7, margin: 0 },
  storyToggle:        { color: '#475569', fontSize: '18px', flexShrink: 0, transition: 'transform 0.2s', marginTop: '2px' },
  storySoWhat:        { background: 'rgba(56,189,248,0.05)', border: '1px solid rgba(56,189,248,0.12)' },
};

function ImpactBadge({ level }) {
  const map = {
    low:    { label: '🟢 Minimal Impact',      color: '#22c55e', bg: 'rgba(34,197,94,0.1)'   },
    medium: { label: '🟡 Watch It',            color: '#f59e0b', bg: 'rgba(245,158,11,0.1)'  },
    high:   { label: '🔴 Action May Be Needed', color: '#ef4444', bg: 'rgba(239,68,68,0.1)'  },
  };
  const m = map[level] || map.low;
  return (
    <span style={{ background: m.bg, color: m.color, border: `1px solid ${m.color}33`, borderRadius: '6px', padding: '3px 10px', fontSize: '11px', fontWeight: '700' }}>
      {m.label}
    </span>
  );
}

const SNAP_LABELS = { BTC: 'Bitcoin', ETH: 'Ethereum', SOL: 'Solana', SPY: 'S&P 500', QQQ: 'Nasdaq', DIA: 'Dow Jones', GLD: 'Gold', '^TNX': '10Y Treasury', '^VIX': 'Volatility' };
const SNAP_EMOJIS = { BTC: '₿', ETH: '⟠', SOL: '◎', SPY: '📈', QQQ: '💻', DIA: '🏭', GLD: '🥇', '^TNX': '🏦', '^VIX': '⚡' };

function SnapCard({ item, blurred }) {
  const pct = parseFloat(item.changePercent);
  const up  = pct >= 0;
  const fmt = item.type === 'rate'
    ? `${item.price?.toFixed(2)}%`
    : item.price > 1000
      ? `$${Math.round(item.price).toLocaleString()}`
      : `$${item.price?.toFixed(2)}`;
  return (
    <div style={{
      ...DS.snapCard,
      borderColor: up ? 'rgba(34,197,94,0.25)' : 'rgba(239,68,68,0.25)',
      background: up ? 'rgba(34,197,94,0.05)' : 'rgba(239,68,68,0.05)',
      filter: blurred ? 'blur(5px)' : 'none',
      userSelect: blurred ? 'none' : 'auto',
      pointerEvents: blurred ? 'none' : 'auto',
    }}>
      <div style={DS.snapEmoji}>{SNAP_EMOJIS[item.symbol] || '📊'}</div>
      <div style={DS.snapSym}>{SNAP_LABELS[item.symbol] || item.symbol}</div>
      <div style={DS.snapPrice}>{fmt}</div>
      <div style={{ ...DS.snapChange, color: up ? '#22c55e' : '#ef4444' }}>
        {up ? '▲' : '▼'} {Math.abs(pct).toFixed(2)}%
      </div>
    </div>
  );
}

function MarketSnapshot({ data, pro }) {
  if (!data || !data.length) return null;
  const free   = data.filter(d => d.type !== 'stock').slice(0, 4);
  const locked = data.filter(d => d.type !== 'stock').slice(4, 7);

  return (
    <div>
      <div style={DS.snapshotGrid}>
        {free.map((item, i) => <SnapCard key={i} item={item} />)}
      </div>

      {!pro && locked.length > 0 && (
        <div style={{ position: 'relative', marginTop: '10px' }}>
          <div style={DS.snapshotGrid}>
            {locked.map((item, i) => <SnapCard key={i} item={item} blurred />)}
          </div>
          <div style={DS.snapUpgradeOverlay}>
            <div style={DS.snapUpgradeInner}>
              <div style={DS.snapUpgradeTitle}>📈 {locked.length} more live tickers</div>
              <div style={DS.snapUpgradeSub}>Gold, Bonds, Volatility Index & individual stocks — with AI explanations</div>
              <Link to="/pricing" style={DS.snapUpgradeBtn}>Unlock Full Markets with Pro →</Link>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StoryCard({ story, idx }) {
  const [open, setOpen] = useState(idx === 0); // first story open by default
  return (
    <div style={{ ...DS.storyCard, ...(open ? DS.storyCardOpen : {}) }}>
      <button style={DS.storyHead} onClick={() => setOpen(o => !o)}>
        <div style={DS.storyHeadLeft}>
          <span style={DS.storyNum}>#{idx + 1}</span>
          <div>
            <div style={DS.storyTitle}>{story.headline}</div>
            <div style={DS.storyMeta}>
              <ImpactBadge level={story.impact} />
              {story.category && <span style={DS.storyCat}>{story.category}</span>}
            </div>
          </div>
        </div>
        <span style={{ ...DS.storyToggle, transform: open ? 'rotate(180deg)' : 'none' }}>▾</span>
      </button>
      {open && (
        <div style={DS.storyBody}>
          <div style={DS.storySection}>
            <div style={DS.storySectionLabel}>📰 What happened</div>
            <p style={DS.storyText}>{story.what}</p>
          </div>
          <div style={DS.storySection}>
            <div style={DS.storySectionLabel}>🔍 Why it happened</div>
            <p style={DS.storyText}>{story.why}</p>
          </div>
          <div style={{ ...DS.storySection, ...DS.storySoWhat }}>
            <div style={DS.storySectionLabel}>💸 What it means for YOUR money</div>
            <p style={DS.storyText}>{story.soWhat}</p>
          </div>
        </div>
      )}
    </div>
  );
}

function StructuredSection({ icon, label, body, accent }) {
  const accentColor = accent || '#38bdf8';
  return (
    <div style={{
      background: 'rgba(15,23,42,0.5)',
      borderRadius: '12px',
      padding: '20px 22px',
      marginBottom: '16px',
      border: `1px solid rgba(${accentColor === '#a78bfa' ? '167,139,250' : '56,189,248'},0.1)`,
    }}>
      <div style={{
        fontSize: '11px', fontWeight: '800', color: accentColor,
        textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '12px',
      }}>
        {icon} {label}
      </div>
      {body?.split('\n').filter(Boolean).map((p, i) => (
        <p key={i} style={{ color: '#94a3b8', fontSize: '14px', lineHeight: 1.75, margin: '0 0 10px' }}>{p}</p>
      ))}
    </div>
  );
}

function MoneyTipBox({ tip }) {
  if (!tip) return null;
  return (
    <div style={{
      background: 'rgba(56,189,248,0.05)',
      border: '1px solid rgba(56,189,248,0.2)',
      borderRadius: '12px',
      padding: '18px 22px',
      display: 'flex',
      gap: '14px',
      alignItems: 'flex-start',
      marginTop: '8px',
    }}>
      <span style={{ fontSize: '22px', flexShrink: 0 }}>💸</span>
      <div>
        <div style={{ fontSize: '11px', fontWeight: '800', color: '#38bdf8', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '7px' }}>
          What This Means for Your Money
        </div>
        <p style={{ color: '#94a3b8', fontSize: '14px', lineHeight: 1.75, margin: 0 }}>{tip}</p>
      </div>
    </div>
  );
}

function KeyMovesSection({ moves }) {
  if (!moves || !moves.length) return null;
  return (
    <div style={{ marginBottom: '16px' }}>
      <div style={{
        fontSize: '11px', fontWeight: '800', color: '#f59e0b',
        textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '12px',
      }}>
        🔍 What Happened & Why
      </div>
      {moves.map((m, i) => (
        <div key={i} style={{
          background: 'rgba(15,23,42,0.5)',
          border: '1px solid rgba(245,158,11,0.12)',
          borderRadius: '10px',
          padding: '16px 18px',
          marginBottom: '10px',
        }}>
          <p style={{ color: '#e2e8f0', fontSize: '14px', fontWeight: '600', margin: '0 0 6px', lineHeight: 1.5 }}>
            {m.what}
          </p>
          <p style={{ color: '#94a3b8', fontSize: '13px', lineHeight: 1.65, margin: '0 0 6px' }}>
            <span style={{ color: '#f59e0b', fontWeight: '700' }}>Why: </span>{m.why}
          </p>
          <p style={{ color: '#94a3b8', fontSize: '13px', lineHeight: 1.65, margin: 0 }}>
            <span style={{ color: '#38bdf8', fontWeight: '700' }}>So what: </span>{m.soWhat}
          </p>
        </div>
      ))}
    </div>
  );
}

function WeeklyDigest({ digest, pro }) {
  if (!digest) return null;

  // Pro wall for free users
  if (!pro) {
    return (
      <div style={DS.proWall}>
        <div style={DS.proWallIcon}>📬</div>
        <h3 style={DS.proWallTitle}>Weekly Digest</h3>
        <p style={DS.proWallDesc}>
          Every Sunday evening: a plain-English recap of the week in markets — what moved, what it means for your wallet, and what to watch next week. A real edge, not just headlines.
        </p>
        <div style={{ background: 'rgba(56,189,248,0.05)', border: '1px solid rgba(56,189,248,0.15)', borderRadius: '10px', padding: '14px 18px', marginBottom: '18px', textAlign: 'left' }}>
          <div style={{ fontSize: '12px', color: '#64748b', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' }}>What you get with Pro</div>
          {['📬 Weekly Digest every Sunday', '📅 Monthly Outlook on the 1st', '📈 Full market data — 20+ tickers', '🤖 Unlimited AI questions', '📚 All courses + AI Q&A'].map(f => (
            <div key={f} style={{ fontSize: '13px', color: '#94a3b8', marginBottom: '5px' }}>✓ {f}</div>
          ))}
        </div>
        <Link to="/pricing" style={{ ...DS.proWallBtn, textDecoration: 'none', display: 'inline-block', marginBottom: '10px' }}>
          Unlock Pro — $12/mo →
        </Link>
        <p style={DS.proWallNote}>Cancel anytime · Instant access</p>
      </div>
    );
  }

  // New structured format
  const hasStructured = digest.recap || digest.outlook;

  return (
    <div style={DS.digestCard}>
      <div style={DS.digestHeader}>
        <div>
          <div style={DS.digestEyebrow}>📬 Weekly Digest</div>
          <h3 style={DS.digestTitle}>{digest.title}</h3>
          <div style={DS.digestDate}>{digest.date}</div>
        </div>
      </div>
      <div style={DS.digestBody}>
        {/* In a Nutshell */}
        {digest.tldr && (
          <div style={{ background: 'linear-gradient(135deg, rgba(56,189,248,0.12), rgba(167,139,250,0.08))', border: '1px solid rgba(56,189,248,0.25)', borderRadius: '12px', padding: '14px 18px', marginBottom: '20px', display: 'block' }}>
            
            <div>
              <div style={{ fontSize: '10px', fontWeight: '800', color: '#38bdf8', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '4px' }}>In a Nutshell</div>
              <p style={{ margin: 0, color: '#e2e8f0', fontSize: '15px', fontWeight: '600', lineHeight: 1.5 }}>{digest.tldr}</p>
            </div>
          </div>
        )}
        {/* Number of the Week */}
        {digest.numberOfTheWeek && (
          <div style={{ background: 'rgba(15,23,42,0.6)', border: '1px solid rgba(99,102,241,0.2)', borderRadius: '12px', padding: '16px 20px', marginBottom: '20px', display: 'flex', flexWrap: 'wrap', gap: '16px', alignItems: 'center' }}>
            <div style={{ textAlign: 'center', minWidth: '80px' }}>
              <div style={{ fontSize: '26px', fontWeight: '800', background: 'linear-gradient(135deg, #38bdf8, #a78bfa)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>{digest.numberOfTheWeek.number}</div>
              <div style={{ fontSize: '10px', color: '#64748b', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: '2px' }}>{digest.numberOfTheWeek.label}</div>
            </div>
            <div style={{ borderLeft: '1px solid rgba(99,102,241,0.2)', paddingLeft: '16px', flex: '1 1 180px', minWidth: 0 }}>
              <div style={{ fontSize: '10px', fontWeight: '800', color: '#a78bfa', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '4px' }}>Number of the Week</div>
              <p style={{ margin: 0, color: '#94a3b8', fontSize: '13px', lineHeight: 1.6 }}>{digest.numberOfTheWeek.context}</p>
            </div>
          </div>
        )}
        {hasStructured ? (
          <>
            {digest.recap && (
              <StructuredSection
                icon="📰"
                label={digest.recap.label || 'This Week in Review'}
                body={digest.recap.body}
                accent="#38bdf8"
              />
            )}
            <KeyMovesSection moves={digest.keyMoves} />
            {digest.outlook && (
              <StructuredSection
                icon="🔭"
                label={digest.outlook.label || 'What to Watch Next Week'}
                body={digest.outlook.body}
                accent="#a78bfa"
              />
            )}
            <MoneyTipBox tip={digest.moneyTip} />
          </>
        ) : (
          // Legacy fallback: plain body text
          digest.body?.split('\n').filter(Boolean).map((p, i) => (
            <p key={i} style={DS.digestPara}>{p}</p>
          ))
        )}
      </div>
    </div>
  );
}

function ArchiveBrowser({ entries, section, pro, renderEntry }) {
  const [open, setOpen]       = useState(false);
  const [selected, setSelected] = useState(null);

  const locked = (section === 'weekly' || section === 'monthly') && !pro;

  if (!entries || entries.length === 0) return null;

  return (
    <div style={DS.archiveWrap}>
      <button style={DS.archiveToggle} onClick={() => setOpen(o => !o)}>
        <span>📚 Past Editions ({entries.length})</span>
        <span style={{ transform: open ? 'rotate(180deg)' : 'none', display: 'inline-block', transition: 'transform 0.2s' }}>▾</span>
      </button>

      {open && (
        <div style={DS.archiveList}>
          {locked ? (
            <div style={{ textAlign: 'center', padding: '24px' }}>
              <div style={{ fontSize: '24px', marginBottom: '8px' }}>🔒</div>
              <div style={{ color: '#94a3b8', fontSize: '14px', marginBottom: '12px' }}>Past {section} editions are a Pro feature.</div>
              <Link to="/signup?plan=pro" style={DS.digestUnlockBtn}>Unlock Pro — $12/mo →</Link>
            </div>
          ) : selected ? (
            <div>
              <button style={DS.archiveBack} onClick={() => setSelected(null)}>← Back to archive list</button>
              {renderEntry(selected)}
            </div>
          ) : (
            entries.map((entry, i) => (
              <button key={i} style={DS.archiveItem} onClick={() => setSelected(entry)}>
                <div style={DS.archiveItemDate}>{entry.date || entry.title}</div>
                {entry.mood && <span style={{ ...DS.moodBadge, ...moodStyle(entry.mood), fontSize: '11px', padding: '2px 8px' }}>{entry.mood}</span>}
                {entry.title && !entry.mood && <div style={DS.archiveItemTitle}>{entry.title}</div>}
                <span style={DS.archiveItemArrow}>→</span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

export default function Brief() {
  const isMobile = useIsMobile();
  const s = {
    page: { background: '#0f172a', color: '#f8fafc', minHeight: '100vh', padding: isMobile ? '48px 16px' : '60px 40px', maxWidth: '900px', margin: '0 auto', position: 'relative' },
    glow: { position: 'absolute', top: '-80px', left: '50%', transform: 'translateX(-50%)', width: '800px', height: '600px', background: 'radial-gradient(ellipse at center, rgba(245,158,11,0.06) 0%, rgba(56,189,248,0.04) 40%, transparent 65%)', pointerEvents: 'none', zIndex: 0 },

    header: { position: 'relative', zIndex: 1, textAlign: 'center', marginBottom: '40px' },
    eyebrow: { display: 'inline-block', background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.25)', color: '#f59e0b', padding: '5px 16px', borderRadius: '20px', fontSize: '12px', fontWeight: '700', marginBottom: '16px', letterSpacing: '0.3px' },
    title: { fontSize: isMobile ? '38px' : '52px', fontWeight: '900', margin: '0 0 10px', letterSpacing: '-1.5px', lineHeight: 1.05, background: 'linear-gradient(135deg, #f8fafc 0%, #94a3b8 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' },
    subtitle: { color: '#64748b', fontSize: '16px', maxWidth: '560px', margin: '0 auto 8px', lineHeight: 1.6 },
    dateBadge: { display: 'inline-block', background: 'rgba(56,189,248,0.08)', border: '1px solid rgba(56,189,248,0.15)', color: '#38bdf8', padding: '4px 14px', borderRadius: '20px', fontSize: '12px', fontWeight: '600', marginTop: '10px', letterSpacing: '0.5px' },

    // Live snapshot
    snapshotSection:   { position: 'relative', zIndex: 1, marginBottom: '32px', background: '#1e293b', borderRadius: '12px', padding: '12px 16px', border: '1px solid #334155' },
    snapshotHeader:    { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' },
    snapshotLabel:     { fontSize: '11px', fontWeight: '700', color: '#64748b', letterSpacing: '0.3px', textTransform: 'uppercase' },
    snapshotSub:       { display: 'none' },
    snapshotFullLink:  { color: '#38bdf8', fontSize: '11px', fontWeight: '600', textDecoration: 'none', whiteSpace: 'nowrap' },
    snapshotGrid:      { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: '7px' },
    snapCard:          { borderRadius: '8px', padding: '9px 8px', border: '1px solid', textAlign: 'center' },
    snapEmoji:         { fontSize: '14px', marginBottom: '3px', lineHeight: 1 },
    snapSym:           { fontSize: '10px', fontWeight: '700', color: '#64748b', letterSpacing: '0.3px', marginBottom: '5px' },
    snapPrice:         { fontSize: '12px', fontWeight: '800', color: '#f8fafc', marginBottom: '2px' },
    snapChange:        { fontSize: '10px', fontWeight: '700' },
    snapshotNote:      { color: '#334155', fontSize: '10px', marginTop: '8px', textAlign: 'right' },
    snapshotLink:      { color: '#38bdf8', textDecoration: 'none' },
    snapUpgradeOverlay:{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(15,23,42,0.8)', backdropFilter: 'blur(2px)', borderRadius: '8px' },
    snapUpgradeInner:  { textAlign: 'center', padding: '8px' },
    snapUpgradeTitle:  { fontSize: '12px', fontWeight: '800', color: '#f1f5f9', marginBottom: '4px' },
    snapUpgradeSub:    { fontSize: '11px', color: '#64748b', marginBottom: '10px' },
    snapUpgradeBtn:    { background: 'linear-gradient(135deg, #38bdf8, #6366f1)', color: '#fff', padding: '7px 14px', borderRadius: '7px', fontWeight: '700', fontSize: '11px', textDecoration: 'none', display: 'inline-block' },

    // Tabs
    tabs: { display: 'flex', gap: '8px', marginBottom: '32px', flexWrap: 'wrap', position: 'relative', zIndex: 1 },
    tab: { background: 'transparent', border: '1px solid #334155', color: '#64748b', padding: '10px 18px', borderRadius: '10px', fontSize: '14px', fontWeight: '500', cursor: 'pointer', fontFamily: 'inherit' },
    tabActive: { background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.35)', color: '#f1f5f9' },
    tabLock: { fontSize: '11px' },

    loading: { textAlign: 'center', color: '#475569', padding: '80px 0' },

    // Date row
    dateRow: { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px', position: 'relative', zIndex: 1 },
    dateLabel: { color: '#475569', fontSize: '13px', fontWeight: '600' },
    moodBadge: { padding: '4px 12px', borderRadius: '6px', fontSize: '12px', fontWeight: '700' },

    intro: { color: '#94a3b8', fontSize: '16px', lineHeight: 1.7, marginBottom: '28px', position: 'relative', zIndex: 1 },

    // Story cards
    stories: { display: 'flex', flexDirection: 'column', gap: '12px', position: 'relative', zIndex: 1 },
    storyCard: { background: '#1e293b', borderRadius: '14px', border: '1px solid #334155', overflow: 'hidden' },
    storyCardOpen: { border: '1px solid rgba(56,189,248,0.2)' },
    storyHead: { width: '100%', background: 'transparent', border: 'none', padding: '20px 24px', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px', cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left' },
    storyHeadLeft: { display: 'flex', alignItems: 'flex-start', gap: '14px', flex: 1 },
    storyNum: { background: 'rgba(56,189,248,0.1)', color: '#38bdf8', borderRadius: '6px', padding: '2px 8px', fontSize: '11px', fontWeight: '800', flexShrink: 0, marginTop: '2px' },
    storyTitle: { fontSize: '16px', fontWeight: '700', color: '#f1f5f9', lineHeight: 1.4, marginBottom: '8px' },
    storyMeta: { display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' },
    storyCat: { color: '#475569', fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.4px' },
    storyToggle: { color: '#475569', fontSize: '18px', flexShrink: 0, transition: 'transform 0.2s', marginTop: '2px' },

    storyBody: { padding: '0 24px 24px 56px', display: 'flex', flexDirection: 'column', gap: '16px' },
    storySection: { borderRadius: '10px', padding: '14px 16px', background: 'rgba(15,23,42,0.5)' },
    storySoWhat: { background: 'rgba(56,189,248,0.05)', border: '1px solid rgba(56,189,248,0.12)' },
    storySectionLabel: { fontSize: '11px', fontWeight: '700', color: '#38bdf8', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' },
    storyText: { color: '#94a3b8', fontSize: '14px', lineHeight: 1.7, margin: 0 },

    // Bottom line
    bottomLine: { background: 'linear-gradient(135deg, rgba(56,189,248,0.08), rgba(99,102,241,0.06))', border: '1px solid rgba(56,189,248,0.2)', borderRadius: '14px', padding: '20px 24px', display: 'flex', gap: '16px', alignItems: 'flex-start', marginTop: '24px', position: 'relative', zIndex: 1 },
    bottomLineIcon: { fontSize: '24px', flexShrink: 0 },
    bottomLineLabel: { fontSize: '11px', fontWeight: '800', color: '#38bdf8', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' },
    bottomLineText: { color: '#94a3b8', fontSize: '14px', lineHeight: 1.7, margin: 0 },

    // Empty state
    emptyState: { textAlign: 'center', padding: '40px 0', position: 'relative', zIndex: 1 },
    emptyIcon: { fontSize: '48px', marginBottom: '16px' },
    emptyTitle: { fontSize: '20px', fontWeight: '700', color: '#f1f5f9', marginBottom: '8px' },
    emptyText: { color: '#64748b', fontSize: '15px', lineHeight: 1.6, marginBottom: '32px' },
    emptyLink: { color: '#38bdf8', textDecoration: 'none' },
    defaultBrief: { textAlign: 'left' },
    defaultBriefTitle: { fontSize: '14px', fontWeight: '700', color: '#475569', marginBottom: '16px', padding: '10px 16px', background: 'rgba(255,255,255,0.03)', borderRadius: '8px', border: '1px solid #1e293b' },

    // Weekly digest
    digestCard: { background: 'linear-gradient(145deg, #1a2744 0%, #1e293b 100%)', borderRadius: '20px', border: '1px solid rgba(56,189,248,0.2)', overflow: 'hidden', position: 'relative', zIndex: 1, boxShadow: '0 4px 32px rgba(56,189,248,0.06)' },
    digestHeader: { padding: '28px 32px 22px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', borderBottom: '1px solid rgba(56,189,248,0.12)', background: 'linear-gradient(135deg, rgba(56,189,248,0.05), transparent)' },
    digestEyebrow: { fontSize: '11px', fontWeight: '700', color: '#38bdf8', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '8px' },
    digestTitle: { fontSize: '22px', fontWeight: '800', color: '#f1f5f9', margin: '0 0 6px', letterSpacing: '-0.3px' },
    digestDate: { color: '#475569', fontSize: '13px' },
    proBadge: { background: 'rgba(56,189,248,0.1)', border: '1px solid rgba(56,189,248,0.2)', color: '#38bdf8', padding: '5px 12px', borderRadius: '8px', fontSize: '12px', fontWeight: '700', flexShrink: 0 },
    digestBody: { padding: '24px 32px' },
    digestPara: { color: '#94a3b8', fontSize: '15px', lineHeight: 1.75, marginBottom: '14px' },
    digestBlur: { position: 'relative' },
    digestBlurInner: { pointerEvents: 'none' },
    digestOverlay: { position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)', background: 'rgba(15,23,42,0.6)', borderRadius: '12px' },
    digestOverlayInner: { textAlign: 'center', padding: '24px' },
    digestLockIcon: { fontSize: '32px', marginBottom: '12px' },
    digestLockText: { color: '#94a3b8', fontSize: '15px', lineHeight: 1.6, marginBottom: '20px', maxWidth: '320px' },
    digestUnlockBtn: { display: 'inline-block', background: '#38bdf8', color: '#0f172a', padding: '12px 28px', borderRadius: '10px', fontWeight: '700', fontSize: '15px', textDecoration: 'none' },
    digestLockNote: { color: '#475569', fontSize: '12px', marginTop: '10px' },

    // Archive banner
    archiveBanner: { background: 'rgba(56,189,248,0.05)', border: '1px solid rgba(56,189,248,0.15)', borderRadius: '14px', padding: '20px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px', marginTop: '20px' },
    archiveDesc: { color: '#64748b', fontSize: '14px', margin: '4px 0 0' },
    archiveBtn: { background: 'transparent', border: '1px solid rgba(56,189,248,0.3)', color: '#38bdf8', padding: '10px 20px', borderRadius: '8px', fontWeight: '600', fontSize: '14px', textDecoration: 'none', whiteSpace: 'nowrap' },

    // Number of the Day box
    numBox: { background: 'rgba(15,23,42,0.6)', border: '1px solid rgba(99,102,241,0.2)', borderRadius: '12px', padding: '16px 20px', display: 'flex', flexWrap: 'wrap', gap: '16px', alignItems: 'center' },
    numLeft: { textAlign: 'center', minWidth: '80px' },
    numRight: { borderLeft: '1px solid rgba(99,102,241,0.2)', paddingLeft: '16px', flex: '1 1 180px', minWidth: 0 },

    // Monthly
    monthlyCard: { background: 'linear-gradient(145deg, #1c1833 0%, #1e293b 100%)', borderRadius: '20px', border: '1px solid rgba(167,139,250,0.2)', padding: '32px', position: 'relative', zIndex: 1, boxShadow: '0 4px 32px rgba(167,139,250,0.06)' },
    monthlyHeader: { marginBottom: '28px', paddingBottom: '22px', borderBottom: '1px solid rgba(167,139,250,0.15)', background: 'linear-gradient(135deg, rgba(167,139,250,0.05), transparent)', borderRadius: '12px 12px 0 0', margin: '-32px -32px 28px', padding: '32px 32px 22px' },
    monthlyEyebrow: { fontSize: '11px', fontWeight: '700', color: '#a78bfa', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '8px' },
    monthlyTitle: { fontSize: '26px', fontWeight: '800', color: '#f1f5f9', margin: '0 0 6px', letterSpacing: '-0.3px' },
    monthlyDate: { color: '#64748b', fontSize: '13px' },

    actionItems: { background: 'rgba(34,197,94,0.06)', border: '1px solid rgba(34,197,94,0.2)', borderRadius: '12px', padding: '20px 24px', marginTop: '24px' },
    actionItemsTitle: { fontSize: '14px', fontWeight: '700', color: '#22c55e', marginBottom: '14px' },
    actionItem: { display: 'flex', alignItems: 'flex-start', gap: '12px', padding: '10px 0', borderBottom: '1px solid rgba(34,197,94,0.1)', color: '#94a3b8', fontSize: '14px', lineHeight: 1.6 },
    actionNum: { background: 'rgba(34,197,94,0.15)', color: '#22c55e', borderRadius: '6px', padding: '2px 8px', fontSize: '11px', fontWeight: '800', flexShrink: 0, marginTop: '2px' },

    // Pro wall
    proWall: { background: 'linear-gradient(135deg, rgba(56,189,248,0.06), rgba(99,102,241,0.06))', border: '2px solid rgba(56,189,248,0.2)', borderRadius: '16px', padding: '40px', textAlign: 'center', position: 'relative', zIndex: 1 },
    proWallIcon: { fontSize: '48px', marginBottom: '16px' },
    proWallTitle: { fontSize: '24px', fontWeight: '800', color: '#f1f5f9', margin: '0 0 12px' },
    proWallDesc: { color: '#94a3b8', fontSize: '16px', lineHeight: 1.65, maxWidth: '480px', margin: '0 auto 24px' },
    proWallFeatures: { display: 'flex', flexDirection: 'column', gap: '8px', maxWidth: '380px', margin: '0 auto 28px', textAlign: 'left' },
    proWallFeat: { color: '#64748b', fontSize: '14px', padding: '6px 0', borderBottom: '1px solid #1e293b' },
    proWallBtn: { display: 'inline-block', background: '#38bdf8', color: '#0f172a', padding: '14px 32px', borderRadius: '10px', fontWeight: '800', fontSize: '16px', textDecoration: 'none' },
    proWallNote: { color: '#334155', fontSize: '12px', marginTop: '12px' },

    // Archive browser
    archiveWrap:   { marginTop: '32px', position: 'relative', zIndex: 1 },
    archiveToggle: { width: '100%', background: 'rgba(255,255,255,0.03)', border: '1px solid #334155', color: '#64748b', padding: '14px 20px', borderRadius: '12px', cursor: 'pointer', fontFamily: 'inherit', fontSize: '14px', fontWeight: '600', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
    archiveList:   { marginTop: '8px', background: '#1e293b', borderRadius: '12px', border: '1px solid #334155', overflow: 'hidden' },
    archiveItem:   { width: '100%', background: 'transparent', border: 'none', borderBottom: '1px solid #0f172a', color: '#94a3b8', padding: '14px 20px', cursor: 'pointer', fontFamily: 'inherit', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '12px', textAlign: 'left' },
    archiveItemDate:  { flex: 1, fontWeight: '600', color: '#f1f5f9', fontSize: '13px' },
    archiveItemTitle: { flex: 1, color: '#64748b', fontSize: '12px' },
    archiveItemArrow: { color: '#334155', fontSize: '14px' },
    archiveBack:   { background: 'transparent', border: 'none', color: '#38bdf8', cursor: 'pointer', fontFamily: 'inherit', fontSize: '13px', padding: '0 0 16px', display: 'block' },

    // Bottom CTA
    ctaBar: { background: 'rgba(99,102,241,0.06)', border: '1px solid rgba(99,102,241,0.2)', borderRadius: '16px', padding: '24px 28px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px', position: 'relative', zIndex: 1, marginTop: '48px' },
    ctaBarDesc: { color: '#64748b', fontSize: '14px', margin: '4px 0 0' },
    ctaBarBtn: { background: '#38bdf8', color: '#0f172a', padding: '12px 24px', borderRadius: '10px', fontWeight: '700', fontSize: '15px', textDecoration: 'none', whiteSpace: 'nowrap' },
  };

  const [brief,    setBrief]    = useState(null);
  const [archive,  setArchive]  = useState({ daily: [], 'daily-premarket': [], 'daily-postclose': [], weekly: [], monthly: [] });

  const [loading,  setLoading]  = useState(true);
  const [tab,      setTab]      = useState('daily');
  const [dailyTab, setDailyTab] = useState('premarket');
  const pro = isPro();

  // Market closes 4 PM ET = 3 PM CST. Postclose brief generates at 4 PM CST.
  // Unlock display at 4 PM CST (hour >= 16).
  const now = new Date();
  const cstHour = new Date(now.toLocaleString('en-US', { timeZone: 'America/Chicago' })).getHours();
  const cstDay  = new Date(now.toLocaleString('en-US', { timeZone: 'America/Chicago' })).getDay();
  const isWeekday = cstDay >= 1 && cstDay <= 5;
  // Unlocks at 4 PM CST on weekdays; on weekends always show (Friday close data is still valid)
  const postCloseAvailable = isWeekday ? cstHour >= 16 : true;

  useEffect(() => {
    Promise.all([
      fetch(`${API}/api/brief`).then(r => r.json()).catch(() => null),
      fetch(`${API}/api/brief/archive`).then(r => r.json()).catch(() => ({ daily: [], 'daily-premarket': [], 'daily-postclose': [], weekly: [], monthly: [] })),
    ]).then(([b, arc]) => {
      setBrief(b);
      setArchive(arc || { daily: [], 'daily-premarket': [], 'daily-postclose': [], weekly: [], monthly: [] });
      setLoading(false);
    });
  }, []);

  const premarket  = brief?.['daily-premarket'];
  const postclose  = brief?.['daily-postclose'];
  const daily   = premarket || brief?.daily; // fall back to legacy daily
  const weekly  = brief?.weekly;
  const monthly = brief?.monthly;

  const renderDailySection = (entry, isPostclose) => {
    if (!entry) return null;
    const accentColor = isPostclose ? '#a78bfa' : '#38bdf8';
    const badgeLabel = isPostclose ? '📉 Close Recap' : '🌅 Pre-Market';
    const badgeBg = isPostclose ? 'rgba(167,139,250,0.08)' : 'rgba(56,189,248,0.08)';
    const badgeBorder = isPostclose ? 'rgba(167,139,250,0.2)' : 'rgba(56,189,248,0.2)';
    return (
      <div style={{ marginBottom: isPostclose ? 0 : '32px' }}>
        {/* Badge */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px', flexWrap: 'wrap' }}>
          {entry.mood && <span style={{ ...s.moodBadge, ...moodStyle(entry.mood) }}>{entry.mood}</span>}
          <span style={{ fontSize: '11px', color: accentColor, background: badgeBg, border: `1px solid ${badgeBorder}`, padding: '3px 10px', borderRadius: '20px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.4px' }}>{badgeLabel}</span>
        </div>
        {/* Hook — sharp opening line */}
        {entry.hook && (
          <p style={{ fontSize: '18px', fontWeight: '800', color: '#f1f5f9', lineHeight: 1.45, margin: '0 0 18px', borderLeft: `4px solid ${accentColor}`, paddingLeft: '16px' }}>{entry.hook}</p>
        )}
        {entry.tldr && <p style={{ fontSize: '15px', fontWeight: '600', color: '#94a3b8', lineHeight: 1.55, margin: '0 0 18px' }}>{entry.tldr}</p>}
        {entry.intro && <p style={{ ...s.intro, marginBottom: '20px' }}>{entry.intro}</p>}
        {isPostclose && entry.recap?.body && (
          <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '14px', padding: '20px 22px', margin: '0 0 20px' }}>
            <div style={{ fontSize: '11px', fontWeight: '700', color: '#a78bfa', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '10px' }}>📰 {entry.recap.label || "Today's Full Breakdown"}</div>
            <p style={{ margin: 0, color: '#94a3b8', fontSize: '14px', lineHeight: 1.8, whiteSpace: 'pre-line' }}>{entry.recap.body}</p>
          </div>
        )}
        {isPostclose && (entry.winners?.length > 0 || entry.losers?.length > 0) && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', margin: '0 0 20px' }}>
            {entry.winners?.length > 0 && (
              <div style={{ background: 'rgba(34,197,94,0.05)', border: '1px solid rgba(34,197,94,0.18)', borderRadius: '12px', padding: '14px 16px' }}>
                <div style={{ fontSize: '11px', fontWeight: '700', color: '#22c55e', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '10px' }}>🏆 Winners</div>
                {entry.winners.map((w, i) => <div key={i} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: i < entry.winners.length-1 ? '8px' : 0 }}><div style={{ fontSize: '12px', color: '#e2e8f0', fontWeight: '600' }}>{w.name}<div style={{ fontSize: '11px', color: '#475569' }}>{w.reason}</div></div><div style={{ fontSize: '13px', fontWeight: '800', color: '#22c55e', marginLeft: '8px', flexShrink: 0 }}>{w.move}</div></div>)}
              </div>
            )}
            {entry.losers?.length > 0 && (
              <div style={{ background: 'rgba(248,113,113,0.05)', border: '1px solid rgba(248,113,113,0.18)', borderRadius: '12px', padding: '14px 16px' }}>
                <div style={{ fontSize: '11px', fontWeight: '700', color: '#f87171', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '10px' }}>📉 Losers</div>
                {entry.losers.map((l, i) => <div key={i} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: i < entry.losers.length-1 ? '8px' : 0 }}><div style={{ fontSize: '12px', color: '#e2e8f0', fontWeight: '600' }}>{l.name}<div style={{ fontSize: '11px', color: '#475569' }}>{l.reason}</div></div><div style={{ fontSize: '13px', fontWeight: '800', color: '#f87171', marginLeft: '8px', flexShrink: 0 }}>{l.move}</div></div>)}
              </div>
            )}
          </div>
        )}
        {entry.stories?.length > 0 && <div style={s.stories}>{entry.stories.map((story, i) => <StoryCard key={i} story={story} idx={i} />)}</div>}
        {!isPostclose && entry.keyLevels?.length > 0 && (
          <div style={{ background: 'rgba(56,189,248,0.05)', border: '1px solid rgba(56,189,248,0.13)', borderRadius: '12px', padding: '16px 20px', margin: '20px 0' }}>
            <div style={{ fontSize: '11px', fontWeight: '700', color: '#38bdf8', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '12px' }}>📊 Key Levels to Watch</div>
            {entry.keyLevels.map((kl, i) => <div key={i} style={{ display: 'flex', gap: '12px', alignItems: 'flex-start', marginBottom: i < entry.keyLevels.length-1 ? '10px' : 0 }}><span style={{ fontSize: '13px', fontWeight: '800', color: '#38bdf8', flexShrink: 0, minWidth: '90px' }}>{kl.asset} {kl.level}</span><span style={{ fontSize: '13px', color: '#94a3b8', lineHeight: 1.5 }}>{kl.note}</span></div>)}
          </div>
        )}
        {isPostclose && entry.tomorrowWatch && (
          <div style={{ background: 'rgba(56,189,248,0.05)', border: '1px solid rgba(56,189,248,0.13)', borderRadius: '12px', padding: '14px 18px', margin: '18px 0' }}>
            <div style={{ fontSize: '11px', fontWeight: '700', color: '#38bdf8', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '8px' }}>👀 {(entry.tomorrowWatch?.label) || "Tomorrow's Radar"}</div>
            {typeof entry.tomorrowWatch === 'string'
              ? <p style={{ margin: 0, color: '#94a3b8', fontSize: '13px', lineHeight: 1.7 }}>{entry.tomorrowWatch}</p>
              : entry.tomorrowWatch?.items?.length > 0
                ? entry.tomorrowWatch.items.map((item, i) => (
                    <div key={i} style={{ marginBottom: i < entry.tomorrowWatch.items.length-1 ? '10px' : 0 }}>
                      <div style={{ fontSize: '13px', fontWeight: '700', color: '#e2e8f0' }}>{item.what}</div>
                      <div style={{ fontSize: '12px', color: '#64748b', lineHeight: 1.5, marginTop: '2px' }}>{item.why}</div>
                    </div>
                  ))
                : null
            }
          </div>
        )}
        {/* WTF Fact — "Did You Know?" */}
        {entry.wtfFact?.fact && (
          <div style={{ background: 'linear-gradient(135deg, rgba(245,158,11,0.12), rgba(251,191,36,0.06))', border: '1px solid rgba(245,158,11,0.3)', borderRadius: '12px', padding: '16px 20px', margin: '18px 0' }}>
            <div style={{ fontSize: '11px', fontWeight: '700', color: '#f59e0b', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '8px' }}>
              {entry.wtfFact.label || '💡 Did You Know?'}
            </div>
            <p style={{ margin: 0, color: '#fde68a', fontSize: '14px', lineHeight: 1.7 }}>{entry.wtfFact.fact}</p>
          </div>
        )}
        {/* Crypto Spotlight */}
        {entry.cryptoSpotlight?.summary && (
          <div style={{ background: 'linear-gradient(135deg, rgba(99,102,241,0.12), rgba(139,92,246,0.06))', border: '1px solid rgba(99,102,241,0.3)', borderRadius: '12px', padding: '16px 20px', margin: '18px 0' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
              <span style={{ fontSize: '15px' }}>₿</span>
              <span style={{ fontSize: '11px', fontWeight: '700', color: '#818cf8', textTransform: 'uppercase', letterSpacing: '0.8px' }}>
                {entry.cryptoSpotlight.label || 'Crypto Spotlight'}
              </span>
              {entry.cryptoSpotlight.btcPrice && (
                <span style={{ marginLeft: 'auto', fontSize: '13px', fontWeight: '700', color: '#e2e8f0' }}>
                  BTC ${entry.cryptoSpotlight.btcPrice}
                  {entry.cryptoSpotlight.btcMove && (
                    <span style={{ marginLeft: '6px', color: String(entry.cryptoSpotlight.btcMove).startsWith('+') ? '#4ade80' : '#f87171' }}>
                      {entry.cryptoSpotlight.btcMove}
                    </span>
                  )}
                </span>
              )}
            </div>
            <p style={{ margin: 0, color: '#c7d2fe', fontSize: '14px', lineHeight: 1.7 }}>{entry.cryptoSpotlight.summary}</p>
            {entry.cryptoSpotlight.context && (
              <p style={{ margin: '10px 0 0', color: '#94a3b8', fontSize: '13px', lineHeight: 1.6, borderTop: '1px solid rgba(99,102,241,0.2)', paddingTop: '10px' }}>{entry.cryptoSpotlight.context}</p>
            )}
          </div>
        )}
        {entry.bottomLine && (
          <div style={{ display: 'flex', gap: '14px', alignItems: 'flex-start', background: `rgba(${isPostclose ? '167,139,250' : '56,189,248'},0.05)`, border: `1px solid rgba(${isPostclose ? '167,139,250' : '56,189,248'},0.15)`, borderRadius: '12px', padding: '16px 18px', margin: '18px 0' }}>
            <span style={{ fontSize: '20px', flexShrink: 0 }}>💡</span>
            <div>
              <div style={{ fontSize: '11px', fontWeight: '700', color: accentColor, textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '6px' }}>Bottom Line</div>
              <p style={{ margin: 0, color: '#94a3b8', fontSize: '13px', lineHeight: 1.7 }}>{entry.bottomLine}</p>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderDailyEntry = (entry) => {
    // entry may be premarket or postclose; show both if dates match
    const premarketArchive = archive['daily-premarket'] || [];
    const postcloseArchive = archive['daily-postclose'] || [];
    const matchingPostclose = postcloseArchive.find(p => p.date === entry.date);
    const matchingPremarket = premarketArchive.find(p => p.date === entry.date);
    const isPre = !entry.winners && !entry.recap;
    const pre = isPre ? entry : matchingPremarket;
    const post = !isPre ? entry : matchingPostclose;
    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
          <span style={{ fontSize: '13px', color: '#475569', fontWeight: '600' }}>📅 {entry.date}</span>
        </div>
        {renderDailySection(pre, false)}
        {post && (
          <div style={{ borderTop: '1px solid rgba(255,255,255,0.07)', paddingTop: '28px', marginTop: '8px' }}>
            {renderDailySection(post, true)}
          </div>
        )}
        {!post && <p style={{ color: '#475569', fontSize: '13px', fontStyle: 'italic' }}>Close Recap not available for this date.</p>}
      </div>
    );
  };

  const renderWeeklyEntry = (entry) => {
    const hasStructured = entry.recap || entry.outlook;
    return (
      <div style={s.digestCard}>
        <div style={s.digestHeader}>
          <div>
            <div style={s.digestEyebrow}>📬 Weekly Digest</div>
            <h3 style={s.digestTitle}>{entry.title}</h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginTop: '6px' }}>
              <div style={s.digestDate}>{entry.date}</div>
              {entry.recap?.label && entry.recap.label !== 'This Week in Review' && (
                <span style={{ fontSize: '11px', fontWeight: '700', color: '#38bdf8', background: 'rgba(56,189,248,0.1)', border: '1px solid rgba(56,189,248,0.2)', borderRadius: '20px', padding: '2px 10px', letterSpacing: '0.3px' }}>
                  {entry.recap.label.split('—')[0].trim()}
                </span>
              )}
            </div>
          </div>
        </div>
        <div style={s.digestBody}>
          {/* Hook — lead sentence, big & bold */}
          {entry.hook && (
            <p style={{ fontSize: '17px', fontWeight: '800', color: '#f1f5f9', lineHeight: 1.45, margin: '0 0 18px', borderLeft: '4px solid #38bdf8', paddingLeft: '16px' }}>{entry.hook}</p>
          )}
          {hasStructured ? (
            <>
              {entry.recap && (
                <StructuredSection icon="📰" label={entry.recap.label || 'This Week in Review'} body={entry.recap.body} accent="#38bdf8" />
              )}
              <KeyMovesSection moves={entry.keyMoves} />
              {entry.outlook && (
                <StructuredSection icon="🔭" label={entry.outlook.label || 'What to Watch Next Week'} body={entry.outlook.body} accent="#a78bfa" />
              )}
              {/* WTF Fact */}
              {entry.wtfFact?.fact && (
                <div style={{ background: 'rgba(251,191,36,0.06)', border: '1px solid rgba(251,191,36,0.18)', borderRadius: '10px', padding: '14px 16px', margin: '16px 0' }}>
                  <div style={{ fontSize: '11px', fontWeight: '700', color: '#f59e0b', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '6px' }}>{entry.wtfFact.label || '💡 Did You Know?'}</div>
                  <p style={{ margin: 0, color: '#fde68a', fontSize: '14px', lineHeight: 1.7 }}>{entry.wtfFact.fact}</p>
                </div>
              )}
              {/* Crypto Spotlight */}
              {entry.cryptoSpotlight?.summary && (
                <div style={{ background: 'rgba(99,102,241,0.06)', border: '1px solid rgba(99,102,241,0.2)', borderRadius: '10px', padding: '14px 16px', margin: '16px 0' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                    <span style={{ fontSize: '11px', fontWeight: '700', color: '#818cf8', textTransform: 'uppercase', letterSpacing: '0.8px' }}>{entry.cryptoSpotlight.label || 'Crypto Spotlight'}</span>
                    {entry.cryptoSpotlight.btcPrice && (
                      <span style={{ fontSize: '12px', color: '#64748b' }}>BTC ${entry.cryptoSpotlight.btcPrice}{entry.cryptoSpotlight.btcMove && <span style={{ marginLeft: '6px', color: String(entry.cryptoSpotlight.btcMove).startsWith('+') ? '#4ade80' : '#f87171' }}>{entry.cryptoSpotlight.btcMove}</span>}</span>
                    )}
                  </div>
                  <p style={{ margin: 0, color: '#c7d2fe', fontSize: '14px', lineHeight: 1.7 }}>{entry.cryptoSpotlight.summary}</p>
                  {entry.cryptoSpotlight.context && (
                    <p style={{ margin: '10px 0 0', color: '#94a3b8', fontSize: '13px', lineHeight: 1.6, borderTop: '1px solid rgba(99,102,241,0.2)', paddingTop: '10px' }}>{entry.cryptoSpotlight.context}</p>
                  )}
                </div>
              )}
              <MoneyTipBox tip={entry.moneyTip} />
            </>
          ) : (
            entry.body?.split('\n').filter(Boolean).map((p, i) => <p key={i} style={s.digestPara}>{p}</p>)
          )}
        </div>
      </div>
    );
  };

  const renderMonthlyEntry = (entry) => {
    const hasStructured = entry.lastMonth || entry.thisMonth;
    return (
      <div style={s.monthlyCard}>
        <div style={s.monthlyHeader}>
          <div style={s.monthlyEyebrow}>📅 Monthly Debrief & Outlook</div>
          <h3 style={s.monthlyTitle}>{entry.title}</h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginTop: '6px' }}>
            <div style={s.monthlyDate}>{entry.date}</div>
            {entry.lastMonth?.label && (
              <span style={{ fontSize: '11px', fontWeight: '700', color: '#f59e0b', background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.2)', borderRadius: '20px', padding: '2px 10px', letterSpacing: '0.3px' }}>
                {entry.lastMonth.label.split('—')[0].trim()} review
              </span>
            )}
            {entry.thisMonth?.label && (
              <span style={{ fontSize: '11px', fontWeight: '700', color: '#a78bfa', background: 'rgba(167,139,250,0.1)', border: '1px solid rgba(167,139,250,0.2)', borderRadius: '20px', padding: '2px 10px', letterSpacing: '0.3px' }}>
                {entry.thisMonth.label.split('—')[0].trim()} outlook
              </span>
            )}
          </div>
        </div>
        {/* Hook */}
        {entry.hook && (
          <p style={{ fontSize: '17px', fontWeight: '800', color: '#f1f5f9', lineHeight: 1.45, margin: '0 0 20px', borderLeft: '4px solid #f59e0b', paddingLeft: '16px' }}>{entry.hook}</p>
        )}
        {/* TL;DR */}
        {entry.tldr && (
          <div style={{ background: 'linear-gradient(135deg, rgba(56,189,248,0.12), rgba(167,139,250,0.08))', border: '1px solid rgba(56,189,248,0.25)', borderRadius: '12px', padding: '14px 18px', marginBottom: '20px' }}>
            <div style={{ fontSize: '10px', fontWeight: '800', color: '#38bdf8', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '4px' }}>In a Nutshell</div>
            <p style={{ margin: 0, color: '#e2e8f0', fontSize: '15px', fontWeight: '600', lineHeight: 1.5 }}>{entry.tldr}</p>
          </div>
        )}
        {hasStructured ? (
          <>
            {entry.lastMonth && (
              <StructuredSection icon="📋" label={entry.lastMonth.label || 'Last Month in Review'} body={entry.lastMonth.body} accent="#f59e0b" />
            )}
            <KeyMovesSection moves={entry.keyMoves} />
            {entry.thisMonth && (
              <StructuredSection icon="🔭" label={entry.thisMonth.label || 'Month Ahead'} body={entry.thisMonth.body} accent="#a78bfa" />
            )}
            {/* WTF Fact */}
            {entry.wtfFact?.fact && (
              <div style={{ background: 'rgba(251,191,36,0.06)', border: '1px solid rgba(251,191,36,0.18)', borderRadius: '10px', padding: '14px 16px', margin: '16px 20px' }}>
                <div style={{ fontSize: '11px', fontWeight: '700', color: '#f59e0b', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '6px' }}>{entry.wtfFact.label || '💡 Did You Know?'}</div>
                <p style={{ margin: 0, color: '#fde68a', fontSize: '14px', lineHeight: 1.7 }}>{entry.wtfFact.fact}</p>
              </div>
            )}
            {/* Crypto Spotlight */}
            {entry.cryptoSpotlight?.summary && (
              <div style={{ background: 'rgba(99,102,241,0.06)', border: '1px solid rgba(99,102,241,0.2)', borderRadius: '10px', padding: '14px 16px', margin: '16px 20px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                  <span style={{ fontSize: '11px', fontWeight: '700', color: '#818cf8', textTransform: 'uppercase', letterSpacing: '0.8px' }}>{entry.cryptoSpotlight.label || 'Crypto Spotlight'}</span>
                  {entry.cryptoSpotlight.btcPrice && (
                    <span style={{ fontSize: '12px', color: '#64748b' }}>BTC ${entry.cryptoSpotlight.btcPrice}{entry.cryptoSpotlight.btcMove && <span style={{ marginLeft: '6px', color: String(entry.cryptoSpotlight.btcMove).startsWith('+') ? '#4ade80' : '#f87171' }}>{entry.cryptoSpotlight.btcMove}</span>}</span>
                  )}
                </div>
                <p style={{ margin: 0, color: '#c7d2fe', fontSize: '14px', lineHeight: 1.7 }}>{entry.cryptoSpotlight.summary}</p>
                {entry.cryptoSpotlight.context && (
                  <p style={{ margin: '10px 0 0', color: '#94a3b8', fontSize: '13px', lineHeight: 1.6, borderTop: '1px solid rgba(99,102,241,0.2)', paddingTop: '10px' }}>{entry.cryptoSpotlight.context}</p>
                )}
              </div>
            )}
            <MoneyTipBox tip={entry.moneyTip} />
          </>
        ) : (
          entry.body?.split('\n').filter(Boolean).map((p, i) => <p key={i} style={s.digestPara}>{p}</p>)
        )}
        {entry.actionItems?.length > 0 && (
          <div style={s.actionItems}>
            <div style={s.actionItemsTitle}>✅ Action Items This Month</div>
            {entry.actionItems.map((item, i) => (
              <div key={i} style={s.actionItem}>
                <span style={s.actionNum}>{i + 1}</span>
                <span>{item}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={s.page}>
      <SEO
        title="The KnowFi Brief — Daily Market Update in Plain English"
        description="Get today's market brief in plain English — what moved, why it matters, and what to do about it. Pre-market setup and close recap, daily."
        path="/brief"
        keywords="daily market brief, daily stock market summary, pre-market brief, market close recap, what happened in the market today, financial news plain English, market update today, investing news explained, daily financial brief"
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: 'The KnowFi Brief — Daily Market Update in Plain English',
          description: 'Pre-market setup and close recap every trading day. What moved, why it matters, and what to do about it — in plain English.',
          url: 'https://knowfi.io/brief',
          publisher: { '@type': 'Organization', name: 'KnowFi', url: 'https://knowfi.io' },
          breadcrumb: {
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://knowfi.io' },
              { '@type': 'ListItem', position: 2, name: 'The KnowFi Brief', item: 'https://knowfi.io/brief' },
            ],
          },
        })}</script>
      </Helmet>
      <div style={s.glow} />

      {/* Header */}
      <div style={s.header}>
        <div style={s.eyebrow}>📰 Daily Market Intelligence</div>
        <h1 style={s.title}>The KnowFi Brief</h1>
        <p style={s.subtitle}>
          What's happening in markets right now — and what it actually means for your money.
          No Wall Street jargon. No panic. Just clarity.
        </p>
        <div style={s.dateBadge}>
          {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
        </div>
      </div>

      {/* Pro upsell banner — only show when not Pro */}
      {!pro && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px',
          background: 'linear-gradient(135deg, rgba(56,189,248,0.08), rgba(167,139,250,0.08))',
          border: '1px solid rgba(56,189,248,0.25)', borderRadius: '12px',
          padding: '14px 18px', marginBottom: '24px', position: 'relative', zIndex: 1,
        }}>
          <span style={{ fontSize: '13px', color: '#94a3b8' }}>
            ✨ <strong style={{ color: '#f1f5f9' }}>Upgrade to Pro</strong> — unlock the Weekly Digest, Monthly Outlook, full market data & more
          </span>
          <Link to="/pricing" style={{
            background: 'linear-gradient(135deg, #38bdf8, #a78bfa)', color: '#0f172a',
            padding: '8px 18px', borderRadius: '8px', fontWeight: '800', fontSize: '13px',
            textDecoration: 'none', whiteSpace: 'nowrap', flexShrink: 0,
          }}>
            See Plans →
          </Link>
        </div>
      )}

      {/* Tabs */}
      <div style={s.tabs}>
        {[
          { id: 'daily',   label: '📅 Daily Brief' },
          { id: 'weekly',  label: '📬 Weekly Digest', pro: true },
          { id: 'monthly', label: '📅 Monthly Outlook', pro: true },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            style={{ ...s.tab, ...(tab === t.id ? s.tabActive : {}) }}>
            {t.label}
            {t.pro && !pro && <span style={s.tabLock}> 🔒</span>}
          </button>
        ))}
      </div>

      {loading && <div style={s.loading}>Loading today's brief...</div>}

      {!loading && tab === 'daily' && (
        <div>
          {/* Daily sub-tab switcher */}
          <div style={{ display: 'flex', gap: '8px', marginBottom: '28px', borderBottom: '1px solid #1e293b', paddingBottom: '14px' }}>
            <button onClick={() => setDailyTab('premarket')} style={{ background: dailyTab === 'premarket' ? 'linear-gradient(135deg,rgba(56,189,248,0.18),rgba(56,189,248,0.08))' : 'transparent', border: dailyTab === 'premarket' ? '1px solid rgba(56,189,248,0.4)' : '1px solid rgba(255,255,255,0.06)', color: dailyTab === 'premarket' ? '#38bdf8' : '#475569', padding: '9px 20px', borderRadius: '10px', fontSize: '13px', fontWeight: '700', cursor: 'pointer' }}>
              🌅 Pre-Market
            </button>
            <button onClick={() => setDailyTab('postclose')} style={{ background: dailyTab === 'postclose' ? 'linear-gradient(135deg,rgba(167,139,250,0.18),rgba(167,139,250,0.08))' : 'transparent', border: dailyTab === 'postclose' ? '1px solid rgba(167,139,250,0.4)' : '1px solid rgba(255,255,255,0.06)', color: dailyTab === 'postclose' ? '#a78bfa' : '#475569', padding: '9px 20px', borderRadius: '10px', fontSize: '13px', fontWeight: '700', cursor: 'pointer' }}>
              📉 Close Recap{!postCloseAvailable && !postclose ? ' ⏳' : ''}
            </button>
          </div>

          {/* PRE-MARKET */}
          {dailyTab === 'premarket' && (
            <div>
              {daily?.date && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '18px', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '13px', color: '#475569', fontWeight: '600' }}>📅 {daily.date}</span>
                  {daily?.mood && <span style={{ ...s.moodBadge, ...moodStyle(daily.mood) }}>{daily.mood}</span>}
                  <span style={{ fontSize: '11px', color: '#38bdf8', background: 'rgba(56,189,248,0.08)', border: '1px solid rgba(56,189,248,0.2)', padding: '3px 10px', borderRadius: '20px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.4px' }}>🌅 Pre-Market</span>
                </div>
              )}
              {daily?.tldr && (
                <p style={{ fontSize: '17px', fontWeight: '700', color: '#e2e8f0', lineHeight: 1.55, margin: '0 0 20px', borderLeft: '3px solid #38bdf8', paddingLeft: '16px' }}>{daily.tldr}</p>
              )}
              {daily?.intro && <p style={{ ...s.intro, marginBottom: '24px' }}>{daily.intro}</p>}
              {daily?.keyLevels?.length > 0 && (
                <div style={{ margin: '0 0 24px', padding: '18px 20px', background: 'rgba(56,189,248,0.04)', border: '1px solid rgba(56,189,248,0.12)', borderRadius: '14px' }}>
                  <div style={{ fontSize: '11px', fontWeight: '700', color: '#38bdf8', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '14px' }}>📊 Key Levels</div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '10px' }}>
                    {daily.keyLevels.map((kl, i) => (
                      <div key={i} style={{ background: 'rgba(15,23,42,0.7)', borderRadius: '10px', padding: '12px 14px', border: '1px solid rgba(255,255,255,0.04)' }}>
                        <div style={{ fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.4px' }}>{kl.asset}</div>
                        <div style={{ fontSize: '18px', fontWeight: '800', color: '#f1f5f9', margin: '3px 0 2px' }}>{kl.level}</div>
                        <div style={{ fontSize: '11px', color: '#475569', lineHeight: 1.4 }}>{kl.note}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {daily?.stories?.length > 0 && (
                <div style={s.stories}>{daily.stories.map((story, i) => <StoryCard key={i} story={story} idx={i} />)}</div>
              )}
              {daily?.watchFor && (
                <div style={{ background: 'rgba(167,139,250,0.05)', border: '1px solid rgba(167,139,250,0.15)', borderRadius: '12px', padding: '16px 20px', margin: '20px 0' }}>
                  <div style={{ fontSize: '11px', fontWeight: '700', color: '#a78bfa', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '8px' }}>👀 Watch For Today</div>
                  <p style={{ margin: 0, color: '#94a3b8', fontSize: '14px', lineHeight: 1.7 }}>{daily.watchFor}</p>
                </div>
              )}
              {daily?.bottomLine && (
                <div style={{ display: 'flex', gap: '14px', alignItems: 'flex-start', background: 'rgba(56,189,248,0.05)', border: '1px solid rgba(56,189,248,0.15)', borderRadius: '12px', padding: '18px 20px', margin: '20px 0' }}>
                  <span style={{ fontSize: '22px', flexShrink: 0 }}>💡</span>
                  <div>
                    <div style={{ fontSize: '11px', fontWeight: '700', color: '#38bdf8', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '6px' }}>Bottom Line</div>
                    <p style={{ margin: 0, color: '#94a3b8', fontSize: '14px', lineHeight: 1.7 }}>{daily.bottomLine}</p>
                  </div>
                </div>
              )}
              {daily?.numberOfTheDay && (
                <div style={{ ...s.numBox, margin: '20px 0' }}>
                  <div style={s.numLeft}>
                    <div style={{ fontSize: '28px', fontWeight: '900', background: 'linear-gradient(135deg,#38bdf8,#a78bfa)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>{daily.numberOfTheDay.number}</div>
                    <div style={{ fontSize: '10px', color: '#475569', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: '2px' }}>{daily.numberOfTheDay.label}</div>
                  </div>
                  <div style={s.numRight}>
                    <p style={{ margin: 0, color: '#94a3b8', fontSize: '13px', lineHeight: 1.6 }}>{daily.numberOfTheDay.context}</p>
                  </div>
                </div>
              )}
              <ShareBar text={(daily?.tldr ? daily.tldr + " — KnowFi Pre-Market Brief" : "Check the KnowFi Pre-Market Brief — what's moving before the open and what it means for your money.")} url="https://knowfi.io/brief" />
              {(() => {
                const premarketEntries = archive['daily-premarket'] || archive.daily || [];
                const postcloseEntries = archive['daily-postclose'] || [];
                const allDates = [...new Set([...premarketEntries.map(e => e.date), ...postcloseEntries.map(e => e.date)])].sort().reverse();
                const mergedEntries = allDates.map(date => premarketEntries.find(e => e.date === date) || postcloseEntries.find(e => e.date === date));
                return <ArchiveBrowser entries={mergedEntries} section="daily-premarket" pro={pro} renderEntry={renderDailyEntry} />;
              })()}
            </div>
          )}

          {/* CLOSE RECAP */}
          {dailyTab === 'postclose' && (
            <div>
              {(() => {
                const postcloseStale = postclose && daily && postclose.date && daily.date && postclose.date !== daily.date;
                if (!postCloseAvailable || postcloseStale) return (
                  <div style={{ textAlign: 'center', padding: '64px 24px' }}>
                    <div style={{ fontSize: '52px', marginBottom: '16px' }}>⏳</div>
                    <h3 style={{ color: '#94a3b8', fontSize: '18px', fontWeight: '700', margin: '0 0 10px' }}>Close Recap updates 1 hour after market close</h3>
                    <p style={{ color: '#475569', margin: 0, fontSize: '14px', lineHeight: 1.6 }}>Check back after 4:00 PM CST. We'll break down what moved and why.</p>
                  </div>
                );
                if (!postclose) return (
                  <div style={{ textAlign: 'center', padding: '64px 24px' }}>
                    <div style={{ fontSize: '52px', marginBottom: '16px' }}>⏳</div>
                    <h3 style={{ color: '#94a3b8', fontSize: '18px', fontWeight: '700', margin: '0 0 10px' }}>Loading close recap…</h3>
                    <p style={{ color: '#475569', margin: 0, fontSize: '14px', lineHeight: 1.6 }}>Hang tight, pulling today's data.</p>
                  </div>
                );
                return null;
              })()}
              {postCloseAvailable && postclose && !(postclose.date && daily?.date && postclose.date !== daily.date) ? (
                <div>
                  {/* Date + mood + badge row */}
                  {postclose?.date && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '18px', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: '13px', color: '#475569', fontWeight: '600' }}>📅 {postclose.date}</span>
                      {postclose?.mood && <span style={{ ...s.moodBadge, ...moodStyle(postclose.mood) }}>{postclose.mood}</span>}
                      <span style={{ fontSize: '11px', color: '#a78bfa', background: 'rgba(167,139,250,0.08)', border: '1px solid rgba(167,139,250,0.2)', padding: '3px 10px', borderRadius: '20px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.4px' }}>📉 Close Recap</span>
                    </div>
                  )}
                  {/* Hook headline — same size/weight as premarket tldr */}
                  {(postclose?.hook || postclose?.tldr) && (
                    <p style={{ fontSize: '17px', fontWeight: '700', color: '#e2e8f0', lineHeight: 1.55, margin: '0 0 20px', borderLeft: '3px solid #a78bfa', paddingLeft: '16px' }}>{postclose.hook || postclose.tldr}</p>
                  )}
                  {/* Intro body paragraph */}
                  {postclose?.intro && <p style={{ ...s.intro, marginBottom: '24px' }}>{postclose.intro}</p>}
                  {/* Winners / Losers */}
                  {(postclose?.winners?.length > 0 || postclose?.losers?.length > 0) && (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', margin: '0 0 24px' }}>
                      {postclose.winners?.length > 0 && (
                        <div style={{ margin: '0', padding: '18px 20px', background: 'rgba(34,197,94,0.04)', border: '1px solid rgba(34,197,94,0.14)', borderRadius: '14px' }}>
                          <div style={{ fontSize: '11px', fontWeight: '700', color: '#22c55e', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '14px' }}>🏆 Winners</div>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: '10px' }}>
                            {postclose.winners.map((w, i) => (
                              <div key={i} style={{ background: 'rgba(15,23,42,0.7)', borderRadius: '10px', padding: '12px 14px', border: '1px solid rgba(34,197,94,0.08)' }}>
                                <div style={{ fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.4px' }}>{w.name}</div>
                                <div style={{ fontSize: '18px', fontWeight: '800', color: '#22c55e', margin: '3px 0 2px' }}>{w.move}</div>
                                <div style={{ fontSize: '11px', color: '#475569', lineHeight: 1.4 }}>{w.reason}</div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {postclose.losers?.length > 0 && (
                        <div style={{ margin: '0', padding: '18px 20px', background: 'rgba(248,113,113,0.04)', border: '1px solid rgba(248,113,113,0.14)', borderRadius: '14px' }}>
                          <div style={{ fontSize: '11px', fontWeight: '700', color: '#f87171', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '14px' }}>📉 Losers</div>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: '10px' }}>
                            {postclose.losers.map((l, i) => (
                              <div key={i} style={{ background: 'rgba(15,23,42,0.7)', borderRadius: '10px', padding: '12px 14px', border: '1px solid rgba(248,113,113,0.08)' }}>
                                <div style={{ fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.4px' }}>{l.name}</div>
                                <div style={{ fontSize: '18px', fontWeight: '800', color: '#f87171', margin: '3px 0 2px' }}>{l.move}</div>
                                <div style={{ fontSize: '11px', color: '#475569', lineHeight: 1.4 }}>{l.reason}</div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  {/* Recap / Full Breakdown */}
                  {postclose?.recap?.body && (
                    <div style={{ margin: '0 0 24px', padding: '18px 20px', background: 'rgba(167,139,250,0.04)', border: '1px solid rgba(167,139,250,0.12)', borderRadius: '14px' }}>
                      <div style={{ fontSize: '11px', fontWeight: '700', color: '#a78bfa', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '14px' }}>📰 {postclose.recap.label || "Today's Full Breakdown"}</div>
                      <p style={{ margin: 0, color: '#94a3b8', fontSize: '14px', lineHeight: 1.8, whiteSpace: 'pre-line' }}>{postclose.recap.body}</p>
                    </div>
                  )}
                  {/* Stories */}
                  {postclose?.stories?.length > 0 && (
                    <div style={s.stories}>{postclose.stories.map((story, i) => <StoryCard key={i} story={story} idx={i} />)}</div>
                  )}
                  {/* Tomorrow's Radar */}
                  {postclose?.tomorrowWatch && (
                    <div style={{ background: 'rgba(167,139,250,0.05)', border: '1px solid rgba(167,139,250,0.15)', borderRadius: '12px', padding: '16px 20px', margin: '20px 0' }}>
                      <div style={{ fontSize: '11px', fontWeight: '700', color: '#a78bfa', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '8px' }}>👀 {postclose.tomorrowWatch?.label || "Tomorrow's Radar"}</div>
                      {typeof postclose.tomorrowWatch === 'string'
                        ? <p style={{ margin: 0, color: '#94a3b8', fontSize: '14px', lineHeight: 1.7 }}>{postclose.tomorrowWatch}</p>
                        : postclose.tomorrowWatch?.items?.length > 0
                          ? postclose.tomorrowWatch.items.map((item, i) => (
                              <div key={i} style={{ marginBottom: i < postclose.tomorrowWatch.items.length - 1 ? '10px' : 0 }}>
                                <div style={{ fontSize: '13px', fontWeight: '700', color: '#e2e8f0' }}>{item.what}</div>
                                <div style={{ fontSize: '12px', color: '#64748b', lineHeight: 1.5, marginTop: '2px' }}>{item.why}</div>
                              </div>
                            ))
                          : null
                      }
                    </div>
                  )}
                  {/* Bottom Line */}
                  {postclose?.bottomLine && (
                    <div style={{ display: 'flex', gap: '14px', alignItems: 'flex-start', background: 'rgba(167,139,250,0.05)', border: '1px solid rgba(167,139,250,0.15)', borderRadius: '12px', padding: '18px 20px', margin: '20px 0' }}>
                      <span style={{ fontSize: '22px', flexShrink: 0 }}>💡</span>
                      <div>
                        <div style={{ fontSize: '11px', fontWeight: '700', color: '#a78bfa', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '6px' }}>Bottom Line</div>
                        <p style={{ margin: 0, color: '#94a3b8', fontSize: '14px', lineHeight: 1.7 }}>{postclose.bottomLine}</p>
                      </div>
                    </div>
                  )}
                  {/* Number of the Day */}
                  {postclose?.numberOfTheDay && (
                    <div style={{ ...s.numBox, margin: '20px 0' }}>
                      <div style={s.numLeft}>
                        <div style={{ fontSize: '28px', fontWeight: '900', background: 'linear-gradient(135deg,#a78bfa,#38bdf8)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>{postclose.numberOfTheDay.number}</div>
                        <div style={{ fontSize: '10px', color: '#475569', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: '2px' }}>{postclose.numberOfTheDay.label}</div>
                      </div>
                      <div style={s.numRight}>
                        <p style={{ margin: 0, color: '#94a3b8', fontSize: '13px', lineHeight: 1.6 }}>{postclose.numberOfTheDay.context}</p>
                      </div>
                    </div>
                  )}
                  {/* WTF Fact */}
                  {postclose?.wtfFact?.fact && (
                    <div style={{ background: 'linear-gradient(135deg, rgba(245,158,11,0.1), rgba(251,191,36,0.05))', border: '1px solid rgba(245,158,11,0.25)', borderRadius: '12px', padding: '16px 20px', margin: '20px 0' }}>
                      <div style={{ fontSize: '11px', fontWeight: '700', color: '#f59e0b', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '8px' }}>{postclose.wtfFact.label || '💡 Did You Know?'}</div>
                      <p style={{ margin: 0, color: '#fde68a', fontSize: '14px', lineHeight: 1.7 }}>{postclose.wtfFact.fact}</p>
                    </div>
                  )}
                  {/* Crypto Spotlight */}
                  {postclose?.cryptoSpotlight?.summary && (
                    <div style={{ background: 'linear-gradient(135deg, rgba(99,102,241,0.1), rgba(139,92,246,0.05))', border: '1px solid rgba(99,102,241,0.25)', borderRadius: '12px', padding: '16px 20px', margin: '20px 0' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                        <span style={{ fontSize: '15px' }}>₿</span>
                        <span style={{ fontSize: '11px', fontWeight: '700', color: '#818cf8', textTransform: 'uppercase', letterSpacing: '0.8px' }}>{postclose.cryptoSpotlight.label || 'Crypto Spotlight'}</span>
                      </div>
                      <p style={{ margin: 0, color: '#c7d2fe', fontSize: '14px', lineHeight: 1.7 }}>{postclose.cryptoSpotlight.summary}</p>
                    </div>
                  )}
                  <ShareBar text={(postclose?.hook || postclose?.tldr ? (postclose.hook || postclose.tldr) + " — KnowFi Close Recap" : "KnowFi Close Recap — winners, losers, and what's on radar tomorrow. Finance explained without the BS.")} url="https://knowfi.io/brief" />
                </div>
              ) : null}
            </div>
          )}
        </div>
      )}

      {!loading && tab === 'weekly' && (
        <div>
          {!pro ? (
            <div style={s.proWall}>
              <div style={s.proWallIcon}>📬</div>
              <h3 style={s.proWallTitle}>Weekly Digest — Pro Exclusive</h3>
              <p style={s.proWallDesc}>
                Every Sunday evening: a plain-English recap of the week in markets — what moved, what it means for your wallet, and what to watch heading into next week.
              </p>
              <div style={s.proWallFeatures}>
                {[
                  'What moved the markets this week & why',
                  'Plain-English breakdown — no Wall Street jargon',
                  'What it actually means for your money',
                  'Key things to watch next week',
                  'Stocks, crypto, rates & economic data covered',
                  'Full archive of past weekly digests',
                ].map((f, i) => (
                  <div key={i} style={s.proWallFeat}>✅ {f}</div>
                ))}
              </div>
              <Link to="/signup?plan=pro" style={s.proWallBtn}>Get KnowFi Pro — $12/mo →</Link>
              <p style={s.proWallNote}>Cancel anytime · All features unlocked immediately</p>
            </div>
          ) : weekly ? (
            <WeeklyDigest digest={weekly} pro={pro} />
          ) : (
            <div style={s.emptyState}>
              <div style={s.emptyIcon}>📬</div>
              <h3 style={s.emptyTitle}>Weekly digest coming soon</h3>
              <p style={s.emptyText}>Published every Sunday evening — a full recap of the week in markets, in plain English.</p>
            </div>
          )}
          {pro && (
            <ArchiveBrowser
              entries={archive.weekly}
              section="weekly"
              pro={pro}
              renderEntry={renderWeeklyEntry}
            />
          )}
        </div>
      )}

      {!loading && tab === 'monthly' && (
        <div>
          {pro ? (
            monthly ? (
              <div style={s.monthlyCard}>
                <div style={s.monthlyHeader}>
                  <div style={s.monthlyEyebrow}>📅 Monthly Debrief & Outlook</div>
                  <h3 style={s.monthlyTitle}>{monthly.title}</h3>
                  <div style={s.monthlyDate}>{monthly.date}</div>
                </div>
                {/* Monthly In a Nutshell */}
                {monthly.tldr && (
                  <div style={{ background: 'linear-gradient(135deg, rgba(56,189,248,0.12), rgba(167,139,250,0.08))', border: '1px solid rgba(56,189,248,0.25)', borderRadius: '12px', padding: '14px 18px', marginBottom: '20px', display: 'block' }}>
                    
                    <div>
                      <div style={{ fontSize: '10px', fontWeight: '800', color: '#38bdf8', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '4px' }}>In a Nutshell</div>
                      <p style={{ margin: 0, color: '#e2e8f0', fontSize: '15px', fontWeight: '600', lineHeight: 1.5 }}>{monthly.tldr}</p>
                    </div>
                  </div>
                )}
                {/* Number of the Month */}
                {monthly.numberOfTheMonth && (
                  <div style={{ background: 'rgba(15,23,42,0.6)', border: '1px solid rgba(99,102,241,0.2)', borderRadius: '12px', padding: '16px 20px', marginBottom: '20px', display: 'flex', flexWrap: 'wrap', gap: '16px', alignItems: 'center' }}>
                    <div style={{ textAlign: 'center', minWidth: '80px' }}>
                      <div style={{ fontSize: '26px', fontWeight: '800', background: 'linear-gradient(135deg, #38bdf8, #a78bfa)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>{monthly.numberOfTheMonth.number}</div>
                      <div style={{ fontSize: '10px', color: '#64748b', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: '2px' }}>{monthly.numberOfTheMonth.label}</div>
                    </div>
                    <div style={{ borderLeft: '1px solid rgba(99,102,241,0.2)', paddingLeft: '16px', flex: '1 1 180px', minWidth: 0 }}>
                      <div style={{ fontSize: '10px', fontWeight: '800', color: '#a78bfa', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '4px' }}>Number of the Month</div>
                      <p style={{ margin: 0, color: '#94a3b8', fontSize: '13px', lineHeight: 1.6 }}>{monthly.numberOfTheMonth.context}</p>
                    </div>
                  </div>
                )}
                {(monthly.lastMonth || monthly.thisMonth) ? (
                  <>
                    {monthly.lastMonth && (
                      <StructuredSection icon="📋" label={monthly.lastMonth.label || 'Last Month in Review'} body={monthly.lastMonth.body} accent="#f59e0b" />
                    )}
                    <KeyMovesSection moves={monthly.keyMoves} />
                    {monthly.thisMonth && (
                      <StructuredSection icon="🔭" label={monthly.thisMonth.label || 'Month Ahead'} body={monthly.thisMonth.body} accent="#a78bfa" />
                    )}
                    <MoneyTipBox tip={monthly.moneyTip} />
                  </>
                ) : (
                  monthly.body?.split('\n').filter(Boolean).map((p, i) => (
                    <p key={i} style={s.digestPara}>{p}</p>
                  ))
                )}
                {monthly.actionItems?.length > 0 && (
                  <div style={s.actionItems}>
                    <div style={s.actionItemsTitle}>✅ Your Action Items This Month</div>
                    {monthly.actionItems.map((item, i) => (
                      <div key={i} style={s.actionItem}>
                        <span style={s.actionNum}>{i + 1}</span>
                        <span>{item}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div style={s.emptyState}>
                <div style={s.emptyIcon}>📅</div>
                <h3 style={s.emptyTitle}>Monthly outlook coming soon</h3>
                <p style={s.emptyText}>Published at the start of each month — a deep dive on where we are and what to watch.</p>
              </div>
            )
          ) : (
            <div style={s.proWall}>
              <div style={s.proWallIcon}>📅</div>
              <h3 style={s.proWallTitle}>Monthly Outlook & Debrief</h3>
              <p style={s.proWallDesc}>
                Every month: a plain-English debrief of what happened, plus a forward-looking outlook — what's coming, what to watch, and what it means for your money.
              </p>
              <div style={{ background: 'rgba(167,139,250,0.05)', border: '1px solid rgba(167,139,250,0.15)', borderRadius: '10px', padding: '14px 18px', marginBottom: '18px', textAlign: 'left' }}>
                <div style={{ fontSize: '12px', color: '#64748b', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' }}>Pro includes</div>
                {['📅 Monthly Debrief + Outlook', '📬 Weekly Digest every Sunday', '📈 Full market data — 20+ tickers', '🤖 Unlimited AI questions', '📚 All courses + AI Q&A'].map(f => (
                  <div key={f} style={{ fontSize: '13px', color: '#94a3b8', marginBottom: '5px' }}>✓ {f}</div>
                ))}
              </div>
              <Link to="/pricing" style={{ ...s.proWallBtn, textDecoration: 'none', display: 'inline-block', marginBottom: '10px' }}>
                Unlock Pro — $12/mo →
              </Link>
              <p style={s.proWallNote}>Cancel anytime · Instant access</p>
            </div>
          )}
          {pro && (
            <ArchiveBrowser
              entries={archive.monthly}
              section="monthly"
              pro={pro}
              renderEntry={renderMonthlyEntry}
            />
          )}
        </div>
      )}

      {/* CTA bar at the bottom */}
      <div style={s.ctaBar}>
        <div>
          <strong style={{ color: '#f1f5f9' }}>📊 Want to see what this means for YOUR situation?</strong>
          <p style={s.ctaBarDesc}>Build your free personalized money plan in 2 minutes — based on your income, debt, and goals.</p>
        </div>
        <Link to="/gameplan" style={s.ctaBarBtn}>Build My Plan →</Link>
      </div>

      {/* Email Capture */}
      <EmailCapture
        variant="card"
        source="brief-bottom"
        title="Never miss the Brief"
        subtitle="Get it free in your inbox every morning."
      />
    </div>
  );
}

function moodStyle(mood) {
  if (!mood) return {};
  const m = mood.toLowerCase();
  if (m.includes('bearish') || m.includes('fear'))   return { background: 'rgba(239,68,68,0.12)',  color: '#f87171',  border: '1px solid rgba(239,68,68,0.25)'  };
  if (m.includes('bullish') || m.includes('green'))  return { background: 'rgba(34,197,94,0.12)',  color: '#4ade80',  border: '1px solid rgba(34,197,94,0.25)'   };
  return { background: 'rgba(245,158,11,0.12)', color: '#fbbf24', border: '1px solid rgba(245,158,11,0.25)' };
}

// Default stories shown when no brief is published yet — pre-written for current market
const DEFAULT_STORIES = [
  {
    headline: 'The Stock Market Is Down — Here\'s the Real Reason Why',
    impact: 'medium',
    category: 'Markets',
    what: 'The S&P 500 is negative year-to-date as of mid-March 2026, having dropped sharply from its all-time highs set in early January. The Nasdaq has been hit even harder, with major tech stocks like Meta, Nvidia, and Tesla all down significantly.',
    why: 'Two main forces are driving the selloff: (1) President Trump escalated global tariffs in February after a Supreme Court ruling, creating uncertainty about costs for US companies. (2) Investors are dumping companies they think will lose to AI — particularly after a Chinese AI company (DeepSeek) showed that AI can be built much cheaper than Wall Street assumed, which threatens the massive spending on Nvidia chips.',
    soWhat: 'If you\'re a long-term investor, this is uncomfortable but normal. The S&P 500 averages a 10% correction every 16 months. Panic-selling turns a paper loss into a real one. If you have cash to invest, downturns are historically the best time to buy — just do it in pieces (dollar-cost averaging) rather than all at once.',
  },
  {
    headline: 'Fed Meeting This Tuesday — What to Expect',
    impact: 'high',
    category: 'Fed & Rates',
    what: 'The Federal Reserve holds its March 17-18 meeting this coming Tuesday and Wednesday. They will decide whether to raise, lower, or hold the federal funds rate (currently at 3.64%).',
    why: 'Markets expect the Fed to hold rates unchanged. Inflation (measured by PCE) has been stuck above the Fed\'s 2% target for over 4 years. The Fed won\'t cut until they\'re confident inflation is truly beaten — despite pressure from the White House to lower rates.',
    soWhat: 'For homebuyers: don\'t expect mortgage rates to drop this week. The 30-year mortgage is still around 6.76% and likely stays elevated through mid-2026. For savers: high-yield savings accounts are still paying ~4-5% — keep your cash there. For investors: "higher for longer" rates tend to hurt tech stocks but benefit financial stocks and short-term bonds.',
  },
  {
    headline: 'Bitcoin & Gold Both Near All-Time Highs — Why?',
    impact: 'low',
    category: 'Crypto & Commodities',
    what: 'While the stock market is in a correction, Bitcoin is holding above $70,000 and Gold has surged past $5,000 per ounce — both near or at all-time highs.',
    why: 'When investors get nervous about stocks and the dollar, they often rotate into "store of value" assets like gold and Bitcoin. This is called a "flight to safety." Oil is also elevated (~$98/barrel) due to ongoing geopolitical tensions and tariff uncertainty affecting supply chains.',
    soWhat: 'If you own Bitcoin or Gold, the current environment is working in your favor. If you\'re thinking about buying, understand these assets don\'t pay dividends or interest — they only go up if someone later pays more than you did. Gold is less volatile; Bitcoin can still drop 50%+ quickly. Don\'t put more in than you can afford to lose completely.',
  },
];

